// src/utils/commonProperties.js

import * as THREE from 'three';

const EPSILON = 1e-6;

/**
 * Round numbers to 3 decimal places to avoid floating-point drift.
 * @param {number} num
 * @returns {number}
 */
const normalize = (num) => {
  if (typeof num !== 'number' || !Number.isFinite(num)) return num;
  return Number(num.toFixed(3));
};

/**
 * Per-type calculators that produce computed properties for a single object.
 * Each returns a Map<key, value>.
 */
const calculators = {
  line: (obj) => {
    const props = new Map();
    const posAttr = obj.geometry?.attributes?.position;
    if (!posAttr || posAttr.count < 2) return props;

    // get two end‐points in world space
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    a.fromBufferAttribute(posAttr, 0);
    b.fromBufferAttribute(posAttr, 1);
    obj.localToWorld(a);
    obj.localToWorld(b);

    const length = normalize(a.distanceTo(b));
    props.set('length', length);
    return props;
  },

  polyline: (obj) => {
    const props = new Map();
    const posAttr = obj.geometry?.attributes?.position;
    if (!posAttr) return props;

    let total = 0;
    const v1 = new THREE.Vector3();
    const v2 = new THREE.Vector3();
    posAttr.count > 0 && v1.fromBufferAttribute(posAttr, 0) && obj.localToWorld(v1);

    for (let i = 1; i < posAttr.count; i++) {
      v2.fromBufferAttribute(posAttr, i);
      obj.localToWorld(v2);
      total += v1.distanceTo(v2);
      v1.copy(v2);
    }

    props.set('vertexCount', posAttr.count);
    props.set('length', normalize(total));
    return props;
  },

  spline: (obj) => {
    const props = new Map();
    const posAttr = obj.geometry?.attributes?.position;
    if (!posAttr) return props;

    const points = [];
    const tmp = new THREE.Vector3();
    for (let i = 0; i < posAttr.count; i++) {
      tmp.fromBufferAttribute(posAttr, i);
      obj.localToWorld(tmp);
      points.push(tmp.clone());
    }

    props.set('vertexCount', points.length);
    if (points.length >= 2) {
      const curve = new THREE.CatmullRomCurve3(points);
      props.set('length', normalize(curve.getLength()));
    }
    return props;
  },

  circle: (obj) => {
    const props = new Map();
    const r = obj.userData.radius;
    if (r == null) return props;

    // factor in uniform world-scale if present
    const scale = obj.getWorldScale(new THREE.Vector3()).x;
    const rr = r * scale;

    props.set('radius', normalize(rr));
    props.set('area', normalize(Math.PI * rr * rr));
    props.set('circumference', normalize(2 * Math.PI * rr));
    return props;
  },

  ellipse: (obj) => {
    const props = new Map();
    const a = obj.userData.majorRadius;
    const b = obj.userData.minorRadius;
    if (a == null || b == null) return props;

    const scale = obj.getWorldScale(new THREE.Vector3());
    const aa = a * scale.x;
    const bb = b * scale.y;

    props.set('majorRadius', normalize(aa));
    props.set('minorRadius', normalize(bb));
    props.set('area', normalize(Math.PI * aa * bb));
    return props;
  },

  rect: (obj) => {
    const props = new Map();
    const w = obj.userData.width;
    const h = obj.userData.height;
    if (w == null || h == null) return props;

    const scale = obj.getWorldScale(new THREE.Vector3());
    const ww = w * scale.x;
    const hh = h * scale.y;

    props.set('width', normalize(ww));
    props.set('height', normalize(hh));
    props.set('area', normalize(ww * hh));
    props.set('perimeter', normalize(2 * (ww + hh)));
    return props;
  },

  mtext: (obj) => {
    const props = new Map();
    if (obj.userData.fontSize != null) {
      props.set('fontSize', normalize(obj.userData.fontSize));
    }
    if (obj.userData.originalColor || obj.userData.color) {
      props.set('color', obj.userData.originalColor || obj.userData.color);
    }
    if (obj.userData.font) {
      props.set('font', obj.userData.font);
    }
    if (obj.userData.alignment != null) {
      props.set('alignment', obj.userData.alignment);
    }
    return props;
  },
};

/**
 * Formats raw keys into human-friendly labels.
 */
const LABELS = {
  length: 'Length',
  vertexCount: 'Vertex Count',
  area: 'Area',
  circumference: 'Circumference',
  perimeter: 'Perimeter',
  majorRadius: 'Major Radius',
  minorRadius: 'Minor Radius',
  width: 'Width',
  height: 'Height',
  radius: 'Radius',
  color: 'Color',
  fontSize: 'Font Size',
  font: 'Font',
  alignment: 'Alignment',
  rotation: 'Rotation',
  angle: 'Angle',
};

export const COMMANDS = {
  // Drawing commands
  DRAW_LINE: 'drawLine',
  DRAW_POLYLINE: 'drawPolyline',
  DRAW_SPLINE: 'drawSpline',
  DRAW_CIRCLE: 'drawCircle',
  DRAW_ARC: 'drawArc',
  DRAW_ELLIPSE: 'drawEllipse',
  DRAW_RECT: 'drawRect',
  
  // Text commands
  DRAW_TEXT: 'drawText',
  DRAW_SINGLE_LINE_TEXT: 'drawSingleLineText',
  DRAW_MTEXT: 'drawMtext',
  
  // Electrical commands
  DRAW_PANEL: 'drawPanel',
  DRAW_MAIN_PANEL: 'drawMainPanel',
  DRAW_CABIN: 'drawCabin',
  DRAW_SOCKET: 'drawSocket',
  DRAW_LIGHT: 'drawLight',
  DRAW_MACHINE: 'drawMachine',
  DRAW_INTERKOM: 'drawInterkom', // 🆕 İnterkom hattı (yeşil, 200W tek faz makine gibi)
  DRAW_WASHING_MACH: 'drawWashingMach',
  DRAW_WEAK_CURRENT_BELL: 'zayıfAkımZil',
  DRAW_ELECTRICAL_WIRE: 'drawElectricalWire',
  DRAW_WIRE: 'drawWire',
  DRAW_FIXTURE: 'drawFixture',
  DRAW_BELL_BUTTON: 'drawBellButton',
  DRAW_DOOR_OPENER: 'drawDoorOpener', // 🆕 Kapı Otomatiği (sadece interkom hattına)
  DRAW_BELL: 'drawBell', // 🆕 Zil tesisatı
  DRAW_CABLE_SHAFT: 'drawCableShaft', // 🆕 Kablo Bacası
  DRAW_SUPPLY_POINT: 'drawSupplyPoint',
  DRAW_WEAK_CURRENT_BUTTON: "DRAW_WEAK_CURRENT_BUTTON",

  
  // Modify commands
  ERASE: 'erase',
  MOVE: 'move',
  COPY: 'copy',
  ROTATE: 'rotate',
  
  
  
  // View commands
  SHOW_SNAPS: 'showSnaps',
  SHOW_GIZMOS: 'showGizmos',
  SHOW_LINES: 'showLines',
  SHOW_OBJECT: 'showObject',
  SHOW_POLYLINES: 'showPolylines',

  // Grounding commands
  DRAW_GROUNDINGSTRIP:"groundingStrip",
  DRAW_MPANEL_GROUNDING:"mPanelGrounding",
  // Grounding commands
  SHOW_ENTITIES:"showEntities",

  // Note commands
  DRAW_CALLOUT: 'drawCallout',

  // Project/Document commands
  SELECT_FLOOR_PLAN: 'selectFloorPlan',
  SELECT_SITE_PLAN: 'selectSitePlan',
  DELETE_FROM_SCENE: 'deleteFromScene',
};

export const DRAW_METHODS = {
  circle: [
    { key: 'center_radius',   label: 'Merkez, Yaricap' },
    { key: 'center_diameter',  label: 'Merkez, Cap' },
    { key: '2point',           label: '2 Nokta' },
    { key: '3point',           label: '3 Nokta' },
  ],
  arc: [
    { key: 'center_start_end', label: 'Merkez, Baslangic, Bitis' },
    { key: '3point',            label: '3 Nokta' },
    { key: 'start_center_end',  label: 'Baslangic, Merkez, Bitis' },
    { key: 'start_end_radius',  label: 'Baslangic, Bitis, Yaricap' },
  ],
  ellipse: [
    { key: 'center_axis',       label: 'Merkez, Eksen, Mesafe' },
    { key: 'axis_endpoints',    label: 'Eksen Uclari, Mesafe' },
  ],
  rect: [
    { key: 'diagonal_corners', label: 'Capraz Koseler' },
    { key: '2corner',          label: '2 Kose' },
    { key: 'center_corner',    label: 'Merkez, Kose' },
  ],
};

const formatPropertyLabel = (key) =>
  LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);

/**
 * Finds common properties among multiple Three.js objects.
 * @param {Array<THREE.Object3D>} objects
 * @returns {Object|null} { type, properties, objectCount, objectTypes } or null
 */
export const findCommonProperties = (objects) => {
  if (!objects || objects.length < 2) return null;

  // Build a Map of computed + userData properties for each object
  const propertyMaps = objects.map((obj) => {
    const base = new Map();
    const ud = obj.userData || {};

    // include all userData keys except internal ones
    const IGNORED = ['id', 'isSelectable', 'originalColor'];
    Object.keys(ud).forEach((k) => {
      if (!IGNORED.includes(k)) base.set(k, ud[k]);
    });

    // merge with computed properties
    const typeKey = ud.type || 'Unknown';
    base.set('type', typeKey);
    const computed = calculators[typeKey]?.(obj) || new Map();
    for (const [k, v] of computed) {
      base.set(k, v);
    }
    return base;
  });

  // Determine which keys appear in every map
  const allKeys = [...propertyMaps[0].keys()];
  const commonKeys = allKeys.filter((key) =>
    propertyMaps.every((m) => m.has(key))
  );

  const objectCount = objects.length;
  const objectTypes = [
    ...new Set(objects.map((o) => o.userData?.type || 'Unknown')),
  ];

  // Build the properties array, with type logic after checking mixed/same
  const properties = [
    {
      label: 'Selection Count',
      type: 'number',
      value: objectCount,
      isCommon: true,
    },
    {
      label: 'Object Types',
      type: 'text',
      value: objectTypes.join(', '),
      isCommon: true,
    },
    ...commonKeys
      .filter((key) => key !== 'type')
      .map((key) => {
        const values = propertyMaps.map((m) => m.get(key));
        const allSame = values.every((v) =>
          typeof v === 'number'
            ? Math.abs(v - values[0]) < EPSILON
            : v === values[0]
        );

        if (allSame) {
          return {
            label: formatPropertyLabel(key),
            type: typeof values[0] === 'number' ? 'number' : 'text',
            value: values[0],
            isCommon: true,
          };
        } else {
          return {
            label: formatPropertyLabel(key),
            type: 'text',
            value: '<Mixed Values>',
            isCommon: false,
            values,
          };
        }
      }),
  ];

  return {
    type: `Multiple Objects (${objectCount})`,
    properties,
    objectCount,
    objectTypes,
  };
};